export type AnalyticsEvent = Record<string, unknown> & { event: string };

export type GaItem = {
    item_id?: string;
    item_name?: string;
    item_type?: string;
    price?: number;
    quantity?: number;
    item_variant?: string;
    item_brand?: string;
    index?: number;
    item_list_id?: string;
    item_list_name?: string;
    discount?: number;
    coupon?: string;
};

type AnalyticsListener = (event: AnalyticsEvent) => void;
export type ShopAnalyticsMode = 'off' | 'legacy' | 'native';

type TronShop = {
    analytics: AnalyticsApi;
    analyticsMode?: ShopAnalyticsMode;
    pendingShares?: { method?: string }[];
    onAddToAnyShare?: (data: { method?: string }) => void;
    /** State of the reCAPTCHA api.js tag rendered by _RecaptchaV3.cshtml; set by that tag's inline handlers. */
    recaptchaApi?: 'loading' | 'loaded' | 'failed';
};

export interface AnalyticsApi {
    on(event: string, cb: AnalyticsListener): void;
    push(event: AnalyticsEvent): void;
    getState(): ReadonlyArray<AnalyticsEvent>;
}

declare global {
    interface Window {
        dataLayer: unknown[];
        gtag?: (...args: unknown[]) => void;
        TronShop?: TronShop;
        a2a_config?: {
            callbacks?: Array<{ share?: (data: { service?: string; node?: Element }) => void }>;
        };
    }
}

const ANALYTICS_VERSION = 1;
/** How long a navigation waits for GTM to process the event pushed just before it (GTM eventCallback). */
export const ANALYTICS_NAVIGATION_DELAY_MS = 300;
const LIST_STORAGE_PREFIX = 'tronshop:analytics:list:';

export function isNativeAnalyticsActive(): boolean {
    return document.body?.getAttribute('data-analytics-native') === 'true';
}

function readShopAnalyticsMode(): ShopAnalyticsMode | undefined {
    const fromWindow = window.TronShop?.analyticsMode;
    if (fromWindow === 'off' || fromWindow === 'legacy' || fromWindow === 'native') {
        return fromWindow;
    }
    const fromBody = document.body?.getAttribute('data-analytics-mode');
    if (fromBody === 'off' || fromBody === 'legacy' || fromBody === 'native') {
        return fromBody;
    }
    return undefined;
}

class AnalyticsHub implements AnalyticsApi {
    private readonly state: AnalyticsEvent[] = [];
    private buffer: AnalyticsEvent[] = [];
    private readonly listeners = new Map<string, AnalyticsListener[]>();

    constructor() {
        this.ingestDataLayer();
    }

    on(event: string, cb: AnalyticsListener): void {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event)!.push(cb);

        const pending = this.buffer.filter(e => e.event === event);
        pending.forEach(e => cb(e));
        if (pending.length > 0) {
            this.buffer = this.buffer.filter(e => e.event !== event);
        }
    }

    push(event: AnalyticsEvent): void {
        const enriched = withEcommerce(event);
        if (isNativeAnalyticsActive()) {
            window.dataLayer = window.dataLayer || [];
            if (isEcommerceEvent(enriched.event)) {
                // GTM merges dataLayer objects. A bare reset (no event name) drops the previous
                // ecommerce object before this event, so "Send ecommerce data → Data layer" does not
                // keep the last event's items. The flat copies are reset too (undefined removes a key
                // from GTM's model), so a container variable never reads an optional field such as
                // coupon or payment_type left over from an earlier event. Not a hub event.
                window.dataLayer.push(ECOMMERCE_RESET);
            }
            window.dataLayer.push(enriched);
        }
        this.dispatch(enriched);
    }

    getState(): ReadonlyArray<AnalyticsEvent> {
        return this.state;
    }

    /**
     * Keep an event in the page state without pushing it to the dataLayer or notifying listeners: a server event
     * whose dedupe key already fired (reloaded checkout, back navigation) must still feed lastCheckoutEvent(), or
     * add_shipping_info / add_payment_info go out with no items and value 0.
     */
    remember(event: AnalyticsEvent): void {
        this.state.push(event);
    }

    /** Resolves once GTM has processed consent_update, or after a short timeout when GTM does not answer. */
    updateConsent(allowedCookieTypes: string[]): Promise<void> {
        if (!isNativeAnalyticsActive()) {
            return Promise.resolve();
        }

        const consent = consentFromCookieTypes(allowedCookieTypes);

        if (typeof window.gtag === 'function') {
            window.gtag('consent', 'update', consent);
        }

        return new Promise<void>(resolve => {
            const done = onceCallback(resolve);
            this.push({ event: 'consent_update', ...consent, eventCallback: done, eventTimeout: ANALYTICS_NAVIGATION_DELAY_MS });
            window.setTimeout(done, ANALYTICS_NAVIGATION_DELAY_MS);
        });
    }

    private dispatch(event: AnalyticsEvent): void {
        this.state.push(event);
        document.dispatchEvent(new CustomEvent('tronshop:analytics', { detail: event }));

        const listeners = this.listeners.get(event.event) ?? [];
        if (listeners.length === 0) {
            this.buffer.push(event);
            return;
        }

        listeners.forEach(cb => cb(event));
    }

    private ingestDataLayer(): void {
        if (!Array.isArray(window.dataLayer)) {
            return;
        }

        for (const entry of window.dataLayer) {
            if (!this.isAnalyticsEvent(entry)) {
                continue;
            }

            this.dispatch(entry);
        }
    }

    private isAnalyticsEvent(entry: unknown): entry is AnalyticsEvent {
        return typeof entry === 'object'
            && entry !== null
            && !Array.isArray(entry)
            && typeof (entry as AnalyticsEvent).event === 'string';
    }
}

/** GA4 ecommerce events. search / login / sign_up / generate_lead / share / consent_update / page_context are not. */
const ECOMMERCE_EVENTS = new Set([
    'view_item',
    'view_item_list',
    'select_item',
    'add_to_cart',
    'remove_from_cart',
    'view_cart',
    'begin_checkout',
    'add_shipping_info',
    'add_payment_info',
    'purchase',
    'view_promotion',
    'select_promotion',
]);

/** Copied under `ecommerce` as well as left flat. `event`, `analytics_version`, and `user_id` stay at the top. */
const ECOMMERCE_FIELDS = [
    'currency',
    'value',
    'items',
    'item_list_id',
    'item_list_name',
    'transaction_id',
    'affiliation',
    'coupon',
    'shipping',
    'tax',
    'shipping_tier',
    'payment_type',
    'promotion_id',
    'promotion_name',
    'creative_slot',
    'creative_name',
    'location_id',
] as const;

const ECOMMERCE_RESET: Record<string, unknown> = ECOMMERCE_FIELDS.reduce<Record<string, unknown>>(
    (reset, key) => {
        reset[key] = undefined;
        return reset;
    },
    { ecommerce: null });

function isEcommerceEvent(eventName: string): boolean {
    return ECOMMERCE_EVENTS.has(eventName);
}

function withEcommerce(event: AnalyticsEvent): AnalyticsEvent {
    if (!isEcommerceEvent(event.event) || event.ecommerce != null) {
        return event;
    }
    const ecommerce: Record<string, unknown> = {};
    for (const key of ECOMMERCE_FIELDS) {
        const value = event[key];
        if (value !== undefined && value !== null) {
            ecommerce[key] = value;
        }
    }
    return { ...event, ecommerce };
}

const analytics = new AnalyticsHub();

window.TronShop = window.TronShop || {} as TronShop;
window.TronShop.analytics = analytics;
const initialMode = readShopAnalyticsMode();
if (initialMode && !window.TronShop.analyticsMode) {
    window.TronShop.analyticsMode = initialMode;
}

/**
 * Consent Mode v2 signals from the shop's cookie categories. Keep in sync with the head bootstrap
 * (Components/Analytics/Default.cshtml), which sets the same map server-side for the initial page load.
 * Marketing drives all three ad signals: ad_user_data / ad_personalization are required alongside
 * ad_storage for Google Ads features in the EEA, and only the storefront (which owns the banner) can set them.
 */
export function consentFromCookieTypes(allowedCookieTypes: string[]): Record<string, 'granted' | 'denied'> {
    const allows = (type: string) => allowedCookieTypes.includes(type);
    const marketing = allows('Marketing') ? 'granted' : 'denied';
    return {
        ad_storage: marketing,
        ad_user_data: marketing,
        ad_personalization: marketing,
        analytics_storage: allows('Tracking') ? 'granted' : 'denied',
        functionality_storage: 'granted',
        security_storage: 'granted',
    };
}

const SERVER_ANALYTICS_ATTR = 'data-tronshop-analytics';

/** Server-built events carry the canonical item ids, so list attribution remembered on the client is re-attached here. */
function enrichServerEvent(event: AnalyticsEvent): AnalyticsEvent {
    if (event.event !== 'add_to_cart' || !Array.isArray(event.items)) {
        return event;
    }
    const items = (event.items as GaItem[]).map(withListAttribution);
    items.forEach(i => rememberItemList(i.item_id, i.item_list_id, i.item_list_name));
    return { ...event, items };
}

/** Push an event the server built (e.g. the cart API's add_to_cart response) into the dataLayer. */
export function pushServerAnalyticsEvent(event: unknown): void {
    if (!isNativeAnalyticsActive()) {
        return;
    }
    if (typeof event !== 'object' || event === null || typeof (event as AnalyticsEvent).event !== 'string') {
        return;
    }
    analytics.push(enrichServerEvent(event as AnalyticsEvent));
}

export function flushServerAnalyticsEvents(root: ParentNode = document): void {
    root.querySelectorAll(`script[${SERVER_ANALYTICS_ATTR}]`).forEach(node => {
        const el = node as HTMLScriptElement;
        el.removeAttribute(SERVER_ANALYTICS_ATTR);

        let event: AnalyticsEvent | null = null;
        try {
            const parsed = JSON.parse(el.textContent || '') as AnalyticsEvent;
            if (parsed && typeof parsed.event === 'string') {
                event = parsed;
            }
        } catch {
        }
        if (!event) {
            return;
        }

        // The key is claimed only for a payload that parsed — a broken payload must not burn the key for the
        // session, or the corrected page would never fire the event.
        let alreadySent = false;
        const dedupeKey = el.getAttribute('data-dedupe-key');
        if (dedupeKey) {
            try {
                alreadySent = !!sessionStorage.getItem(dedupeKey);
                if (!alreadySent) {
                    sessionStorage.setItem(dedupeKey, '1');
                }
            } catch {
            }
        }

        if (alreadySent) {
            analytics.remember(event);
        } else {
            analytics.push(enrichServerEvent(event));
        }
    });
}

flushServerAnalyticsEvents();

/** The returned promise settles when the consent_update event has been handed to GTM; reload after it. */
export function updateAnalyticsConsent(allowedCookieTypes: string[]): Promise<void> {
    return analytics.updateConsent(allowedCookieTypes);
}

function onceCallback(callback: () => void): () => void {
    let called = false;
    return () => {
        if (!called) {
            called = true;
            callback();
        }
    };
}

export function pushAnalytics(event: AnalyticsEvent): void {
    if (!isNativeAnalyticsActive()) {
        return;
    }
    analytics.push(attachUserId({ analytics_version: ANALYTICS_VERSION, ...event }));
}

/**
 * body[data-analytics-user-id] is rendered only when the admin toggle is on, the user is logged in AND Tracking
 * consent is granted (AnalyticsIdentity.ResolveForPayloadAsync). Every cookie-banner choice reloads the page, so
 * the attribute is the single source — there is no same-page "pending" id.
 */
function attachUserId(event: AnalyticsEvent): AnalyticsEvent {
    if (event.user_id != null && event.user_id !== '') {
        return event;
    }
    const userId = document.body?.getAttribute('data-analytics-user-id');
    return userId ? { ...event, user_id: userId } : event;
}

function readAttr(el: Element | null, ...names: string[]): string | undefined {
    if (!el) {
        return undefined;
    }
    for (const name of names) {
        const value = el.getAttribute(name);
        if (value != null && value !== '') {
            return value;
        }
    }
    return undefined;
}

function parseNumber(value: string | undefined): number | undefined {
    if (value == null || value === '') {
        return undefined;
    }
    const n = Number(value.replace(',', '.'));
    return Number.isFinite(n) ? n : undefined;
}

function readRevenueBasis(): 'follow' | 'vat' | 'net' {
    const fromBody = document.body?.getAttribute('data-analytics-revenue-basis');
    if (fromBody === 'vat' || fromBody === 'net') {
        return fromBody;
    }
    return 'follow';
}

function readItemPrice(el: Element): number | undefined {
    const basis = readRevenueBasis();
    // data-ga-price-vat / -net carry the full-precision unit price converted to the visitor's currency. The
    // CDN-script contract attributes data-analytics-price(-vat) are raw primary-currency figures (same
    // 4-decimal precision), so they are only a fallback: 1000 x 0.2345 must report 234.50, not 230.00
    // (ticket 13223). The shop-display figure (data-ga-price / data-product-price) is the last resort so a
    // card that lacks the vat/net pair still reports a price instead of none.
    const vat = readAttr(el, 'data-ga-price-vat', 'data-analytics-price-vat');
    const net = readAttr(el, 'data-ga-price-net', 'data-analytics-price');
    const display = () => parseNumber(readAttr(el, 'data-ga-price', 'data-product-price'));
    if (basis === 'vat') {
        // A shop with VAT display off has no VAT figure at all (PriceRangeVat is null); use the net
        // unit price then, exactly like the server-side items generator does for view_item / lists.
        return parseNumber(vat ?? net) ?? display();
    }
    if (basis === 'net') {
        return parseNumber(net) ?? display();
    }

    if (vat != null || net != null) {
        const shown = el.closest('[data-analytics-price-basis]')?.getAttribute('data-analytics-price-basis');
        if (shown === 'vat') {
            return parseNumber(vat ?? net) ?? display();
        }
        if (shown === 'net') {
            return parseNumber(net ?? vat) ?? display();
        }
    }

    return display();
}

/** Build a GA4 item from data-ga-* (preferred) or legacy data-product-* attributes. */
export function itemFromElement(el: Element | null): GaItem | null {
    if (!el) {
        return null;
    }

    const preferPtn = document.body?.getAttribute('data-analytics-item-id-basis') === 'ptn';
    const itemId = readAttr(
        el,
        'data-ga-item-id',
        ...(preferPtn
            ? ['data-product-ptn-code', 'data-product-shop-code', 'data-product-own-code', 'data-product-supplier-code']
            : ['data-product-shop-code', 'data-product-own-code', 'data-product-supplier-code', 'data-product-ptn-code']));
    const itemName = readAttr(el, 'data-ga-item-name', 'data-product-title');
    const price = readItemPrice(el);
    const quantity = parseNumber(readAttr(el, 'data-ga-quantity')) ?? 1;
    const variant = readAttr(el, 'data-ga-variant');
    const brand = readAttr(el, 'data-ga-item-brand');
    const itemType = readAttr(el, 'data-ga-item-type');
    const index = parseNumber(readAttr(el, 'data-ga-index'));
    const listId = readAttr(el, 'data-ga-list-id');
    const listName = readAttr(el, 'data-ga-list-name');

    if (!itemId && !itemName) {
        return null;
    }

    const item: GaItem = {};
    if (itemId) item.item_id = itemId;
    if (itemName) item.item_name = itemName;
    if (price != null) item.price = price;
    if (quantity != null) item.quantity = quantity;
    if (variant) item.item_variant = variant;
    if (brand) item.item_brand = brand;
    if (itemType) item.item_type = itemType;
    if (index != null) item.index = index;
    if (listId) item.item_list_id = listId;
    if (listName) item.item_list_name = listName;
    return item;
}

export function rememberItemList(itemId: string | undefined, listId?: string, listName?: string): void {
    if (!itemId || (!listId && !listName)) {
        return;
    }
    try {
        sessionStorage.setItem(
            LIST_STORAGE_PREFIX + itemId,
            JSON.stringify({ item_list_id: listId, item_list_name: listName }));
    } catch {
        /* private mode / quota */
    }
}

export function getRememberedItemList(itemId: string | undefined): Pick<GaItem, 'item_list_id' | 'item_list_name'> | undefined {
    if (!itemId) {
        return undefined;
    }
    try {
        const raw = sessionStorage.getItem(LIST_STORAGE_PREFIX + itemId);
        if (!raw) {
            return undefined;
        }
        return JSON.parse(raw) as Pick<GaItem, 'item_list_id' | 'item_list_name'>;
    } catch {
        return undefined;
    }
}

function withListAttribution(item: GaItem): GaItem {
    if (item.item_list_id || item.item_list_name) {
        return item;
    }
    const remembered = getRememberedItemList(item.item_id);
    return remembered ? { ...item, ...remembered } : item;
}

export function getPageCurrency(): string {
    const fromBody = document.body?.getAttribute('data-currency');
    if (fromBody) {
        return fromBody;
    }
    const state = analytics.getState();
    for (let i = state.length - 1; i >= 0; i--) {
        const currency = state[i].currency;
        if (typeof currency === 'string' && currency) {
            return currency;
        }
    }
    return '';
}

/**
 * The latest cart-shaped event of this page (including a deduped begin_checkout kept via remember()). Its server
 * value is items + auto-charges - coupons in the page currency, which is what add_shipping_info / add_payment_info
 * report so the checkout funnel carries one value; summing the items is only the fallback.
 */
function lastCheckoutEvent(): { items: GaItem[]; value: number | undefined } {
    const state = analytics.getState();
    for (let i = state.length - 1; i >= 0; i--) {
        const ev = state[i];
        if (ev.event === 'view_cart' || ev.event === 'begin_checkout' || ev.event === 'add_to_cart') {
            const items = ev.items;
            if (Array.isArray(items) && items.length > 0) {
                const gaItems = items as GaItem[];
                const value = typeof ev.value === 'number' && Number.isFinite(ev.value) ? ev.value : sumItemValue(gaItems);
                return { items: gaItems, value };
            }
        }
    }
    return { items: [], value: undefined };
}

/**
 * Sum of price x quantity, or undefined when no item carries a price (a shop that hides prices sends none):
 * GA4's value is that sum, so an absent price must not become value 0.
 */
function sumItemValue(items: GaItem[]): number | undefined {
    const priced = items.filter(item => typeof item.price === 'number' && Number.isFinite(item.price));
    if (priced.length === 0) {
        return undefined;
    }
    const sum = priced.reduce((acc, item) => acc + item.price * (item.quantity ?? 1), 0);
    return Math.round(sum * 100) / 100;
}

/** Currency and value for a cart-shaped event; both omitted when there is no value (GA4: value needs currency). */
function valueParams(value: number | undefined): Partial<AnalyticsEvent> {
    return value === undefined ? {} : { currency: getPageCurrency(), value };
}

export function trackSelectItem(card: Element): void {
    const item = itemFromElement(card);
    if (!item) {
        return;
    }
    rememberItemList(item.item_id, item.item_list_id, item.item_list_name);
    const payload: AnalyticsEvent = {
        event: 'select_item',
        items: [withListAttribution(item)],
    };
    if (item.item_list_id) payload.item_list_id = item.item_list_id;
    if (item.item_list_name) payload.item_list_name = item.item_list_name;
    pushAnalytics(payload);
}

export function trackRemoveFromCart(items: GaItem[]): void {
    const enriched = items.filter(i => i.item_id || i.item_name);
    if (enriched.length === 0) {
        return;
    }
    pushAnalytics({
        event: 'remove_from_cart',
        ...valueParams(sumItemValue(enriched)),
        items: enriched,
    });
}

export function trackAddShippingInfo(shippingTier: string): void {
    const { items, value } = lastCheckoutEvent();
    pushAnalytics({
        event: 'add_shipping_info',
        shipping_tier: shippingTier,
        ...valueParams(value),
        items,
    });
}

export function trackAddPaymentInfo(paymentType: string): void {
    const { items, value } = lastCheckoutEvent();
    pushAnalytics({
        event: 'add_payment_info',
        payment_type: paymentType,
        ...valueParams(value),
        items,
    });
}

export function trackSearch(searchTerm: string): void {
    if (!searchTerm) {
        return;
    }
    pushAnalytics({ event: 'search', search_term: searchTerm });
}

export function trackViewPromotion(el: Element): void {
    const promotionId = readAttr(el, 'data-ga-promotion-id');
    const promotionName = readAttr(el, 'data-ga-promotion-name');
    if (!promotionId && !promotionName) {
        return;
    }
    pushAnalytics({
        event: 'view_promotion',
        promotion_id: promotionId,
        promotion_name: promotionName,
        creative_slot: readAttr(el, 'data-ga-creative-slot'),
    });
}

export function trackSelectPromotion(el: Element): void {
    const promotionId = readAttr(el, 'data-ga-promotion-id');
    const promotionName = readAttr(el, 'data-ga-promotion-name');
    if (!promotionId && !promotionName) {
        return;
    }
    pushAnalytics({
        event: 'select_promotion',
        promotion_id: promotionId,
        promotion_name: promotionName,
        creative_slot: readAttr(el, 'data-ga-creative-slot'),
    });
}

export function trackLogin(method = 'password'): void {
    pushAnalytics({ event: 'login', method });
}

export function trackSignUp(method = 'password'): void {
    pushAnalytics({ event: 'sign_up', method });
}

/**
 * GA4 recommended lead event for a successfully submitted storefront inquiry / contact form. Named
 * generate_lead (not form_submit) so it is a first-class key event / Ads conversion and does not collide
 * with the enhanced-measurement form_submit GA4 collects on the native submit event.
 */
export function trackGenerateLead(formId: string, formDestination?: string): void {
    if (!formId) {
        return;
    }
    const payload: AnalyticsEvent = { event: 'generate_lead', form_id: formId };
    if (formDestination) {
        payload.form_destination = formDestination;
    }
    pushAnalytics(payload);
}

export function trackShare(method: string, itemId?: string): void {
    if (!method) {
        return;
    }
    const payload: AnalyticsEvent = { event: 'share', method };
    if (itemId) {
        payload.content_type = 'product';
        payload.item_id = itemId;
    }
    pushAnalytics(payload);
}

let addToAnyShareHooked = false;

function hookAddToAnyShare(): void {
    if (addToAnyShareHooked) {
        return;
    }
    addToAnyShareHooked = true;
    window.TronShop = window.TronShop || {} as TronShop;
    window.TronShop.onAddToAnyShare = data => {
        const method = data?.method;
        if (!method) {
            return;
        }
        const detail = document.querySelector('[data-component="ProductDetailMain"]');
        const item = itemFromElement(detail);
        trackShare(method, item?.item_id);
    };
    const pending = window.TronShop.pendingShares;
    if (pending?.length) {
        pending.forEach(share => window.TronShop!.onAddToAnyShare!(share));
        window.TronShop.pendingShares = [];
    }
}

/** One view/select per promotion id/name per page — Slick infinite clones duplicate DOM nodes. */
const viewedPromotionKeys = new Set<string>();
let lastSelectPromotion: { key: string; at: number } | null = null;

function promotionDedupeKey(el: Element): string | null {
    const promotionId = readAttr(el, 'data-ga-promotion-id');
    const promotionName = readAttr(el, 'data-ga-promotion-name');
    if (!promotionId && !promotionName) {
        return null;
    }
    return promotionId ?? promotionName ?? null;
}

function isSlickClone(el: Element): boolean {
    return !!el.closest('.slick-cloned');
}

/** Impression tracking for carousel slides and intersection observer (deduped by promotion id/name). */
export function trackPromotionBannerImpression(el: Element | null | undefined): void {
    if (!el) {
        return;
    }
    const key = promotionDedupeKey(el);
    if (!key || viewedPromotionKeys.has(key)) {
        return;
    }
    viewedPromotionKeys.add(key);
    trackViewPromotion(el);
}

function shouldTrackSelectPromotion(el: Element): boolean {
    const key = promotionDedupeKey(el);
    if (!key) {
        return false;
    }
    const now = Date.now();
    if (lastSelectPromotion?.key === key && now - lastSelectPromotion.at < 500) {
        return false;
    }
    lastSelectPromotion = { key, at: now };
    return true;
}

function observePromotions(root: ParentNode = document): void {
    const banners = Array.from(
        root.querySelectorAll('.campaign-banner__item[data-ga-promotion-id], .campaign-banner__item[data-ga-promotion-name]'),
    ).filter(el => !isSlickClone(el));

    const markViewedAndTrack = (el: Element): void => {
        trackPromotionBannerImpression(el);
    };

    if (!('IntersectionObserver' in window)) {
        banners.forEach(markViewedAndTrack);
        return;
    }

    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            if (!entry.isIntersecting) {
                return;
            }
            markViewedAndTrack(entry.target);
            observer.unobserve(entry.target);
        });
    }, { threshold: 0.5 });

    banners.forEach(el => observer.observe(el));
}

let interactionsInitialized = false;

export function initAnalyticsInteractions(): void {
    if (interactionsInitialized) {
        observePromotions();
        hookAddToAnyShare();
        return;
    }
    interactionsInitialized = true;

    document.addEventListener('click', event => {
        const target = event.target as Element | null;
        if (!target) {
            return;
        }

        const promo = target.closest('.campaign-banner__item') as Element | null;
        if (promo) {
            if (shouldTrackSelectPromotion(promo)) {
                trackSelectPromotion(promo);
            }
            return;
        }

        const card = target.closest('[data-component="ProductListItem"], [data-component="ProductCollection"] [data-ga-item-id], [data-component="SearchBarResult"] [data-ga-item-id]') as Element | null;
        const link = target.closest('a');
        if (card && link && card.contains(link)) {
            trackSelectItem(card);
        }
    }, true);

    document.addEventListener('submit', event => {
        const form = event.target as HTMLFormElement | null;
        if (!form) {
            return;
        }
        const searchInput = form.querySelector('#js-search-autocomplete-input, input[name="q"]') as HTMLInputElement | null;
        if (!searchInput || !form.contains(searchInput)) {
            return;
        }
        trackSearch((searchInput.value || '').trim());
    }, true);

    observePromotions();
    hookAddToAnyShare();
}

if (typeof document !== 'undefined') {
    hookAddToAnyShare();
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => initAnalyticsInteractions());
    } else {
        initAnalyticsInteractions();
    }
}
